import { MediaRequestStatus as MediaRequestStatusV2 } from '@cobliteam/api-lib/src/__generated__/graphql';
import {
  type CreateMediaRequest,
  type CreateMediaRequestResponse,
  type CreateSharedMedia,
  type Media,
  type MediaEvent,
  type MediaRequest,
  MediaRequestStatus,
  MediaRequestStatusFilterV2,
  MediaType,
  type MediaView,
  MediaWatchStatus,
  type PageableContent,
  type PageableContentAPI,
  type SeverityDetail,
} from '@cobliteam/common-types';
import camelcaseKeys from 'camelcase-keys';
import type {
  CreateMediaRequestAPI,
  CreateMediaRequestResponseAPI,
  CreateSharedMediaRequestAPI,
  MediaAPI,
  MediaEventVideoAPI,
  MediaFootageVideoAPI,
  MediaViewAPI,
} from './definitions/APITypes';

const toMediaRequestStatus: { [key: string]: MediaRequestStatus } = {
  PENDING: MediaRequestStatus.PENDING,
  ACKNOWLEDGE: MediaRequestStatus.ACKNOWLEDGE,
  STARTED: MediaRequestStatus.STARTED,
  FINISHED: MediaRequestStatus.FINISHED,
  UNAVAILABLE: MediaRequestStatus.UNAVAILABLE,
  FAILED: MediaRequestStatus.FAILED,
  CANCELED: MediaRequestStatus.CANCELED,
  CORRUPTED: MediaRequestStatus.CORRUPTED,
};

export const mediaNewFilter = (mediaStatus?: MediaWatchStatus[]) => {
  if (mediaStatus) {
    const isNewFilter = mediaStatus.includes(MediaWatchStatus.NEW);
    const isWatchedFilter = mediaStatus.includes(MediaWatchStatus.WATCHED);
    if (isNewFilter && !isWatchedFilter) {
      return true;
    }
    if (!isNewFilter && isWatchedFilter) {
      return false;
    }
  }

  return undefined;
};

export const mediaRequestStatusV2Filter = (mediaStatus?: MediaRequestStatusFilterV2[]): MediaRequestStatusV2[] | undefined => {
  const result = new Set<MediaRequestStatusV2>();

  if (!mediaStatus) {
    return undefined;
  }
  for (const status of mediaStatus) {
    switch (status) {
      case MediaRequestStatusFilterV2.PENDING: {
        result.add(MediaRequestStatusV2.PendingRequest);
        result.add(MediaRequestStatusV2.Requested);
        result.add(MediaRequestStatusV2.ManualRetryRequest);
        result.add(MediaRequestStatusV2.RetryRequested);
        break;
      }
      case MediaRequestStatusFilterV2.PROCESSING:
        result.add(MediaRequestStatusV2.RequestAcknowledged);
        break;
      case MediaRequestStatusFilterV2.COMPLETED:
        result.add(MediaRequestStatusV2.FinishedRequest);
        break;
      case MediaRequestStatusFilterV2.FAILED: {
        result.add(MediaRequestStatusV2.CorruptedVideo);
        result.add(MediaRequestStatusV2.AmountExceeded);
        result.add(MediaRequestStatusV2.FailedRequest);
        result.add(MediaRequestStatusV2.LmErrorFailed);
        result.add(MediaRequestStatusV2.LmErrorUnavailable);
        result.add(MediaRequestStatusV2.PeriodUnavailable);
        result.add(MediaRequestStatusV2.TimeoutRequest);
        break;
      }
      default:
        break;
    }
  }

  return Array.from(result);
};

const statusToMediaRequestStatus = (status: string): MediaRequestStatus => toMediaRequestStatus[status] || MediaRequestStatus.PENDING;

export const toMedia = (mediaAPI: MediaAPI): Media => {
  const commonFields = {
    id: mediaAPI.id,
    deviceId: mediaAPI.device_id,
    driverId: mediaAPI.driver_id,
    unreviewedDriver: mediaAPI.unreviewed_driver,
    driver: mediaAPI.driver,
    mediaType: mediaAPI.media_type,
    mediaStatus: mediaAPI.media_status,
    mediaViewCount: mediaAPI.media_view_count,
    timestamp: mediaAPI.timestamp,
    licensePlate: mediaAPI.vehicle?.licensePlate,
    isFavorite: mediaAPI.is_favorite,
    vehicle: mediaAPI.vehicle,
    vehicleGroup: mediaAPI.vehicle_group,
    safetyReview: mediaAPI.safety_review ? camelcaseKeys(mediaAPI.safety_review, { deep: true }) : undefined,
    comments: mediaAPI.comments ? camelcaseKeys(mediaAPI.comments, { deep: true }) : [],
    aiSuggestedDriver: mediaAPI.ai_suggested_driver ? camelcaseKeys(mediaAPI.ai_suggested_driver, { deep: true }) : undefined,
    aiVideoTags: mediaAPI.ai_video_tags ? camelcaseKeys(mediaAPI.ai_video_tags, { deep: true }) : undefined,
  };

  switch (mediaAPI.media_type) {
    case MediaType.TIMELAPSE:
    case MediaType.FOOTAGE_VIDEO: {
      const videoFootageAPI = mediaAPI.media_details as MediaFootageVideoAPI;
      const createdBy = videoFootageAPI.created_by
        ? {
            id: videoFootageAPI.created_by.id,
            fleetId: videoFootageAPI.created_by.fleet_id,
            emailAddress: videoFootageAPI.created_by.email_address,
            fullName: videoFootageAPI.created_by.full_name,
          }
        : undefined;

      const createdByTrigger = videoFootageAPI.created_by_trigger
        ? {
            id: videoFootageAPI.created_by_trigger.id,
            name: videoFootageAPI.created_by_trigger.name,
          }
        : undefined;

      return {
        ...commonFields,
        mediaDetails: {
          createdAt: videoFootageAPI.created_at,
          processedAt: videoFootageAPI.processed_at,
          duration: videoFootageAPI.duration,
          endLatitude: videoFootageAPI.end_latitude,
          endLongitude: videoFootageAPI.end_longitude,
          mediaUrl: videoFootageAPI.footage_media_url,
          requestId: videoFootageAPI.request_id,
          requestStatus: videoFootageAPI.request_status,
          startLatitude: videoFootageAPI.start_latitude,
          startLongitude: videoFootageAPI.start_longitude,
          status: statusToMediaRequestStatus(videoFootageAPI.status),
          thumbnailUrl: videoFootageAPI.footage_thumbnail_url,
          updatedAt: videoFootageAPI.updated_at,
          createdBy,
          createdByTrigger,
        } as MediaRequest,
      };
    }

    case MediaType.EVENT_VIDEO: {
      const videoEventAPI = mediaAPI.media_details as MediaEventVideoAPI;

      return {
        ...commonFields,
        severity: mediaAPI.severity,
        severityDetail: mediaAPI.severity_detail ? (camelcaseKeys(mediaAPI.severity_detail, { deep: true }) as SeverityDetail) : undefined,
        mediaDetails: {
          eventTrigger: videoEventAPI.event_trigger,
          latitude: videoEventAPI.latitude,
          longitude: videoEventAPI.longitude,
          mediaUrl: videoEventAPI.event_media_url,
          speed: videoEventAPI.speed,
          speedSign: videoEventAPI.speed_sign,
          thumbnailUrl: videoEventAPI.event_thumbnail_url,
        } as MediaEvent,
      };
    }
  }
};

export const toMediaPage = (page: PageableContentAPI<MediaAPI>): PageableContent<Media> => ({
  content: page.content.map(toMedia),
  last: page.last,
  totalPages: page.total_pages,
  totalElements: page.total_elements,
  numberOfElements: page.number_of_elements,
  number: page.number,
  size: page.size,
  first: page.first,
  empty: page.empty,
});

export const toCreateMediaRequestResponse = (response: CreateMediaRequestResponseAPI): CreateMediaRequestResponse => ({
  vehicleId: response.vehicle_id,
  startTime: response.start_time,
  endTime: response.end_time,
  mediaId: response.media_id,
});

export const toMediaView = (response: MediaViewAPI): MediaView => ({
  mediaUrl: response.media_url,
  expirationDate: response.expiration_date,
  mediaInfo: toMedia(response.media_info),
  videos: response.videos,
  aiDescription: response.ai_description,
  suggestedOccurrences: response.suggested_occurrences,
  aiVideoTags: response.ai_video_tags ? camelcaseKeys(response.ai_video_tags, { deep: true }) : undefined,
});

export const toCreateMediaRequestAPI = (request: CreateMediaRequest): CreateMediaRequestAPI => ({
  vehicle_id: request.vehicleId,
  start_time: request.startTime,
  end_time: request.endTime,
  is_timelapse: request.isTimelapse,
  enable_email_notification: request.enableEmailNotification,
  trip_id: request.tripId,
});

export const toCreateSharedMediaRequestAPI = (request: CreateSharedMedia): CreateSharedMediaRequestAPI => ({
  media_id: request.mediaId,
});
